import type { CriterionCard } from '../schemas/criterion.js';
import type {
  ProtocolProductionBlocker,
  ProtocolStepDraft,
  ProtocolStepProblem,
} from '../schemas/protocol.js';
import { DEFAULT_VERSION_PIN, type VersionPin, pinKey } from '../schemas/versioning.js';
import { orGroupProblems } from './criterion-groups.js';
import { farewellEligible } from './farewell.js';
import { rangementConsigneOrphan } from './rangement.js';
import { directionSitesOf, variablesOf } from './variables.js';

/**
 * What a protocol's version REFUSES, computed on the content alone (§7.1), and
 * what its passage to production refuses, computed against the criteria it
 * cites (§4, strict resolution).
 *
 * ## Why these two rules live in `shared`
 *
 * Both sides run them. The server refuses the version and the pointer move
 * with them - that is the guard. The editor runs the same functions to draw the
 * « N problèmes » pill, to disable the confirm button of the two boxes and to
 * list what is wrong, one link per step - that is the courtesy. Two
 * implementations of one rule set would drift, and the drift would show as a
 * box that lets a click through to a 409 nobody expected, or that greys a
 * button the server would have accepted. `structurallyEqual` was duplicated
 * across the two packages for a two-line comparison; a rule set this size is
 * not.
 *
 * ## The rules are the letter of §7.1 plus two of the same kind
 *
 * A step without a turn cap, an objective without a criterion, a jump or a
 * fallback aimed at an absent step: the three the spec names. A trigger that
 * listens to an objective the protocol no longer holds, and an « invalider »
 * action that names one, are the same defect - a dangling reference inside the
 * content - met through another door (H22, §16). Since #163 a farewell step
 * is exempt from the cap and refused when it carries objectives or conditions
 * (`farewell-step-not-empty`, H77), and since #165 refused when it also
 * rereads on entry (`reread-on-farewell-step`, H93), and since #191 when it hides
 * its objectives (`hide-objectives-on-farewell-step`, H19). Since #181 an « l'un de »
 * group of a single criterion is refused too (`or-group-too-small`, H2).
 *
 * The functions take the DRAFT shape of a step, which every version also
 * satisfies: the editor runs them on what is being typed.
 *
 * The two refusals of the « Rangement » section (issue #177) name no step and
 * live beside their rule, in `rangement.ts` (`rangementProblems`): the callers
 * that refuse a version run both.
 */

/** The steps as the editor and the server both hold them. */
export type ProtocolSteps = readonly ProtocolStepDraft[];

export function protocolProblems(steps: ProtocolSteps): ProtocolStepProblem[] {
  const problems: ProtocolStepProblem[] = [];
  const stepIds = new Set(steps.map((step) => step.id));
  const objectiveIds = new Set(steps.flatMap((step) => step.objectives.map((o) => o.id)));

  steps.forEach((step, stepIndex) => {
    const where = { stepId: step.id, stepIndex, stepName: step.name };

    // A farewell step has no cap and asks for none (§7.1, H77): it plays ONE
    // half-turn and closes the protocol, so a ceiling would bound nothing. What
    // it must not carry is objectives or conditions - nobody would judge them.
    if (step.farewell) {
      if (!farewellEligible(step)) {
        problems.push({ kind: 'farewell-step-not-empty', ...where });
      }
      // Nothing to reread for (issue #165, H93): a farewell step has no
      // objective, and the reread only ever fills objectives.
      if (step.rereadOnEntry) {
        problems.push({ kind: 'reread-on-farewell-step', ...where });
      }
      // Nothing to hide either (issue #191, H19): a farewell step has no
      // objective for the interviewer to see.
      if (step.hideObjectives) {
        problems.push({ kind: 'hide-objectives-on-farewell-step', ...where });
      }
    } else if (step.rules.maxTurns === null) {
      problems.push({ kind: 'step-without-cap', ...where });
    }

    for (const objective of step.objectives) {
      if (objective.criteria.length === 0) {
        problems.push({
          kind: 'objective-without-criterion',
          ...where,
          objectiveId: objective.id,
          objectiveName: objective.name,
        });
      }
      // A group « l'un de » of a single citation (issue #181, H2): refused like
      // an objective without a criterion, by the same door and with the same place.
      for (const orGroup of orGroupProblems(objective)) {
        problems.push({
          kind: 'or-group-too-small',
          ...where,
          objectiveId: objective.id,
          objectiveName: objective.name,
          orGroup,
        });
      }
    }

    step.conditions.forEach((condition, conditionIndex) => {
      const place = { ...where, conditionId: condition.id, conditionIndex };
      for (const action of condition.actions) {
        if (action.kind === 'go-to-step' && !stepIds.has(action.stepId)) {
          problems.push({ kind: 'jump-to-missing-step', ...place });
        }
        if (action.kind === 'invalidate-objective' && !objectiveIds.has(action.objectiveId)) {
          problems.push({ kind: 'action-on-missing-objective', ...place });
        }
      }
      for (const group of condition.groups) {
        for (const trigger of group) {
          if (trigger.kind === 'objective-status' && !objectiveIds.has(trigger.objectiveId)) {
            problems.push({ kind: 'trigger-on-missing-objective', ...place });
          }
        }
      }
    });

    // Not on a farewell step: the engine ignores its rules (H76), so a fallback
    // stored on one aims at nothing that will ever be played, and refusing the
    // version over it would refuse a jump that cannot happen.
    if (
      !step.farewell &&
      step.rules.fallback.kind === 'go-to-step' &&
      !stepIds.has(step.rules.fallback.stepId)
    ) {
      problems.push({ kind: 'fallback-to-missing-step', ...where });
    }
  });

  return problems;
}

/** The problems of ONE step - what the frise's yellow triangle counts. */
export function stepProblems(steps: ProtocolSteps, stepId: string): ProtocolStepProblem[] {
  return protocolProblems(steps).filter((problem) => problem.stepId === stepId);
}

/**
 * One step whose directions cite an objective the content does not carry
 * (§8.6 of the refonte, noyau.md §4.9; issue #158): its own direction, one of
 * its objectives', or one of its « direction d'IA » actions - named by the
 * step, the unit the boxes of the protocol and of the experience speak in.
 */
export type ProtocolOrphanStep = {
  readonly stepId: string;
  readonly stepIndex: number;
  readonly stepName: string;
};

/**
 * The ORPHAN objective citations of a content (noyau.md §4.9, V2, V5; issue
 * #158): a `{{objectif:id}}` whose id names no objective of any step, because
 * the objective was deleted or the token was pasted from another protocol.
 */
export type ProtocolOrphans = {
  /** Does the reading for the analyst cite one? It then reads « (objectif retiré) » (V3). */
  readonly reading: boolean;
  /** Does the consigne of the rangement cite one? Same régime as the reading (#177, §4). */
  readonly rangement: boolean;
  /** Each step whose directions cite one, once, in the order of the steps. */
  readonly directionSteps: readonly ProtocolOrphanStep[];
};

/**
 * Walks the reading and every direction site of `steps` for objective tokens
 * nothing answers (§4.9, §8.6). A criterion token is not judged here: a
 * criterion the base lost is a refusal of « Passer en production » already
 * (`productionBlockers`), and the reading cites objectives only (N48).
 *
 * What the protocol's warnings read (`protocolWarnings`, N52) and what the
 * lookup of an experience's gesture reads on the version its tag points at
 * (`ExperienceReferenceState.orphanDirections`, N53): one walk, so the two
 * boxes cannot disagree on which step cites a ghost.
 */
export function protocolOrphans(
  steps: ProtocolSteps,
  analystReading: string,
  rangementConsigne: string,
): ProtocolOrphans {
  const objectiveIds = new Set(steps.flatMap((step) => step.objectives.map((o) => o.id)));
  const citesGhost = (text: string): boolean =>
    variablesOf(text).some(
      (variable) => variable.kind === 'objective' && !objectiveIds.has(variable.id),
    );

  const directionSteps: ProtocolOrphanStep[] = [];
  const named = new Set<number>();
  for (const site of directionSitesOf(steps)) {
    if (named.has(site.stepIndex) || !citesGhost(site.text)) continue;
    named.add(site.stepIndex);
    directionSteps.push({
      stepId: site.stepId,
      stepIndex: site.stepIndex,
      stepName: steps[site.stepIndex]?.name ?? '',
    });
  }
  return {
    reading: citesGhost(analystReading),
    rangement: rangementConsigneOrphan(steps, rangementConsigne),
    directionSteps,
  };
}

/**
 * A structural risk that never blocks a save (§8.4): a condition whose actions
 * carry a `go-to-step` with no `direction` alongside it.
 *
 * `historyOf` (`packages/backend/src/engine/context.ts`) hands the interviewer
 * the WHOLE page's transcript regardless of which step plays next, on purpose -
 * a step reached by advancing needs to see what was already said. A
 * `go-to-step` can instead jump BACKWARD, after the interviewer has already
 * wound the conversation toward a close on the step it is leaving; with
 * nothing telling it otherwise, it may keep that tone rather than open the
 * step it just landed on (observed in production, session `8c0b5d66`,
 * 2026-09-18: a repli to Accueil inherited three turns of goodbyes and never
 * recovered).
 *
 * This is not a `ProtocolProblem`: the content is valid and the version it
 * describes runs. It is not a `ProtocolProductionBlocker` either: it names no
 * criterion and needs no lookup, so it is checked at DEVELOPMENT save time
 * already, through the same walk as `protocolProblems`. And unlike both of its
 * neighbours it carries no Zod schema - a `ProtocolWarning` never travels
 * inside an HTTP error body, `check_protocol` turns it into a sentence in the
 * very call that computes it, and the protocol's boxes in the frame they draw,
 * so there is no wire boundary to validate at.
 *
 * ## The orphan chips (issue #158, N52)
 *
 * Two more, and the protocol WARNS about them without ever refusing (V2): the
 * reading for the analyst cites an objective the version no longer carries -
 * the analyst will read « (objectif retiré) » in its place -, and a direction
 * does, named by its step - « Passer en production » of every experience that
 * plays that version will be refused while it is there (§8.6 of the refonte,
 * V5, V6). The save box, the confirmation of « Passer en production », « Reculer
 * développement » and a rollback from the history say them (N52, the régime of
 * N37).
 */
export type ProtocolWarning =
  | {
      readonly kind: 'go-to-step-without-direction';
      readonly stepId: string;
      readonly stepIndex: number;
      readonly stepName: string;
      readonly conditionId: string;
      readonly conditionIndex: number;
    }
  | ProtocolOrphanWarning;

/**
 * The warnings of an orphan chip (issue #158, and #177 for the consigne of the
 * rangement) - see `ProtocolWarning`.
 */
export type ProtocolOrphanWarning =
  | { readonly kind: 'reading-variable-orphan' }
  | ({ readonly kind: 'direction-variable-orphan' } & ProtocolOrphanStep)
  | { readonly kind: 'rangement-variable-orphan' };

/**
 * The orphan chips of a content as warnings (§4.9, N52): the reading's once -
 * the sentence cannot name an objective that no longer exists, so two ghosts
 * in one text are one line -, then one per step whose directions cite one,
 * then the consigne of the rangement's once (#177, scenario-succes.md §4): the
 * order of the sections of the editor's toggle, the new one last.
 */
export function protocolOrphanWarnings(
  steps: ProtocolSteps,
  analystReading: string,
  rangementConsigne: string,
): ProtocolOrphanWarning[] {
  const orphans = protocolOrphans(steps, analystReading, rangementConsigne);
  return [
    ...(orphans.reading ? [{ kind: 'reading-variable-orphan' as const }] : []),
    ...orphans.directionSteps.map((step) => ({
      kind: 'direction-variable-orphan' as const,
      ...step,
    })),
    ...(orphans.rangement ? [{ kind: 'rangement-variable-orphan' as const }] : []),
  ];
}

/**
 * Every warning of a content (§8.4, §4.9): the orphan chips first, in the
 * order the boxes list them (the maquette `LectureVariablesOrpheline`: the
 * reading, then each step), then every condition whose `go-to-step` has no
 * `direction` action beside it - see `ProtocolWarning`.
 */
export function protocolWarnings(
  steps: ProtocolSteps,
  analystReading: string,
  rangementConsigne: string,
): ProtocolWarning[] {
  const warnings: ProtocolWarning[] = protocolOrphanWarnings(
    steps,
    analystReading,
    rangementConsigne,
  );

  steps.forEach((step, stepIndex) => {
    const where = { stepId: step.id, stepIndex, stepName: step.name };
    step.conditions.forEach((condition, conditionIndex) => {
      const jumps = condition.actions.some((action) => action.kind === 'go-to-step');
      const directed = condition.actions.some((action) => action.kind === 'direction');
      if (jumps && !directed) {
        warnings.push({
          kind: 'go-to-step-without-direction',
          ...where,
          conditionId: condition.id,
          conditionIndex,
        });
      }
    });
  });

  return warnings;
}

/**
 * What a caller knows about one criterion when it checks a passage to
 * production: the facts the strict resolution of §4 depends on, plus what each
 * pointed version says about its notation. The server reads them off the
 * criteria table and their pointed contents, the editor off the criteria cards
 * it already holds - one rule, two lookups.
 *
 * `scored` is per VERSION because a threshold is posed on a CITATION (§7.2):
 * the same criterion may carry a notation in one version and none in another,
 * and which one applies is what `{ id, pin }` says. `latest` is read on the
 * development version - « latest dev » - and `byVersion` holds the versions a
 * caller knows the notation of, by number; a pin it does not list is a citation
 * it makes no claim on (the editor knows the two pointed versions of a card, the
 * server knows them all).
 */
export type CriterionState = {
  readonly name: string;
  readonly hasProduction: boolean;
  readonly retiredAt: string | null;
  readonly scored: {
    readonly latest: boolean;
    readonly byVersion?: Readonly<Record<number, boolean>>;
  };
};

/**
 * The state of a criterion as its CARD says it - what the editor and the
 * assistant, which read the library's cards and not the base, hand to
 * `productionBlockers`. A card knows the notation of the two versions the
 * pointers sit on, so `latest` (the development one) and those two numbers are
 * answered, and a citation pinned to any other version is one the card makes no
 * claim on (`scoredAtPin`); the server, which reads every version, claims them
 * all.
 */
export function criterionStateOfCard(card: CriterionCard): CriterionState {
  const byVersion: Record<number, boolean> = {
    [card.development.number]: card.scoring.development !== null,
  };
  if (card.production !== null)
    byVersion[card.production.number] = card.scoring.production !== null;
  return {
    name: card.name,
    // The POINTER, and never a count of versions: §4 makes « passé en
    // production » the position of a pointer.
    hasProduction: card.production !== null,
    retiredAt: card.retiredAt,
    scored: { latest: card.scoring.development !== null, byVersion },
  };
}

/**
 * Whether the version a pin resolves to carries a notation, or `undefined` when
 * the state does not say (a pinned version the caller did not read).
 */
export function scoredAtPin(state: CriterionState, pin: VersionPin): boolean | undefined {
  return pin === 'latest' ? state.scored.latest : state.scored.byVersion?.[pin];
}

/**
 * The pin each criterion is cited at, by criterion id (vitrine.md §3.1, H14): a
 * `{{critere:id}}` token has no place for a version, so it reads as the
 * requirement of the SAME criterion in the same version of the protocol, the
 * first one when several cite it, and as `latest` when only a token cites it.
 */
export function criterionPinsOf(steps: ProtocolSteps): Map<string, VersionPin> {
  const pins = new Map<string, VersionPin>();
  for (const step of steps) {
    for (const objective of step.objectives) {
      for (const requirement of objective.criteria) {
        if (!pins.has(requirement.ref.id)) pins.set(requirement.ref.id, requirement.ref.pin);
      }
    }
  }
  return pins;
}

export type CriterionLookup = (criterionId: string) => CriterionState | undefined;

/**
 * What « Passer en production » refuses (§4, §7.1, §7.2): every `latest`
 * citation of a criterion with no production version, every citation of a
 * retired criterion whatever its pin, every citation of an id the base does not
 * hold, and every THRESHOLD posed on a criterion the cited version does not
 * score.
 *
 * A pinned citation needs no production pointer: it names that very version,
 * which the cascade of « Passer en production » carries (vitrine.md §4.2); the
 * `latest` check is the one the gesture still owes until the cascade lands.
 *
 * A `{{critere:id}}` variable is a citation at the pin of the same criterion's
 * requirement, `latest` when none (`criterionPinsOf`, H14), and it is reported
 * with a null objective name.
 *
 * The fourth refusal comes from the grill of 2026-09-16 and is a different kind
 * from the first three: the protocol would RUN. H47 (§8.2) says the engine
 * ignores such a threshold and judges the criterion on its verdict, so nothing
 * breaks - the protocol simply does not do what its author wrote, silently, for
 * as long as nobody opens the objective. This is the door where that is caught,
 * and the yellow line on the card (`unscoredThresholds`) is the same fact one
 * screen earlier.
 *
 * It is reported only for a criterion that EXISTS and is not retired: a
 * criterion the base has lost has a threshold on nothing at all, and naming two
 * defects for one citation would send the operator to fix the second one first.
 *
 * Deduplicated by (criterion, step, objective): a criterion cited twice in the
 * same objective's direction is one line, not two.
 */
export function productionBlockers(
  steps: ProtocolSteps,
  lookup: CriterionLookup,
): ProtocolProductionBlocker[] {
  const blockers: ProtocolProductionBlocker[] = [];
  const seen = new Set<string>();

  const check = (
    criterionId: string,
    pin: VersionPin,
    where: { stepId: string; stepIndex: number; stepName: string; objectiveName: string | null },
  ): CriterionState | undefined => {
    // NUL as the separator: the one character no id and no name can contain,
    // written as an escape so git keeps the file text (`source-hygiene.test.ts`).
    const key = `${criterionId}\u0000${where.stepId}\u0000${where.objectiveName ?? ''}`;
    const state = lookup(criterionId);
    // The STATE is rendered whatever the dedup decides, so the walk below never
    // resolves the same criterion a second time.
    if (seen.has(key)) return state;
    const place = { criterionId, criterionName: state?.name ?? criterionId, ...where };
    if (state === undefined) {
      seen.add(key);
      blockers.push({ kind: 'criterion-unknown', ...place });
      return state;
    }
    if (state.retiredAt !== null) {
      seen.add(key);
      blockers.push({ kind: 'criterion-retired', ...place, retiredAt: state.retiredAt });
      return state;
    }
    if (pin === 'latest' && !state.hasProduction) {
      seen.add(key);
      blockers.push({ kind: 'criterion-without-production', ...place });
    }
    return state;
  };

  steps.forEach((step, stepIndex) => {
    for (const objective of step.objectives) {
      for (const requirement of objective.criteria) {
        const where = {
          stepId: step.id,
          stepIndex,
          stepName: step.name,
          objectiveName: objective.name,
        };
        const state = check(requirement.ref.id, requirement.ref.pin, where);

        const threshold = requirement.threshold;
        // The cheapest question first: most citations pose no threshold at all,
        // and everything below - a key to build, a state to read - is owed only
        // by the ones that do.
        if (threshold === null) continue;
        /*
          Skipped when the citation ALREADY has a blocker of its own: an unknown
          id, a retired criterion, or a `latest` pointing at no production
          version. Each is one gesture to repair, and the threshold is a
          consequence of it rather than a second thing to fix - a criterion never
          passed to production is not « unscored in production », it resolves to
          nothing at all there. The card's own line reads this same walk, so the
          two can never name different faults for one citation.

          A pin the state says nothing about (a version the caller did not read)
          is skipped too: no claim is better than a wrong one.
        */
        const resolves =
          state !== undefined &&
          state.retiredAt === null &&
          (requirement.ref.pin !== 'latest' || state.hasProduction);
        if (!resolves || scoredAtPin(state, requirement.ref.pin) !== false) continue;
        /*
          Its own key, and the three parts JSON-encoded rather than joined on a
          separator: the walk above needs a character no id can contain and uses
          NUL for it, which this file may hold only as an escape
          (`source-hygiene.test.ts`). An encoded array needs no such character
          at all, and the leading word keeps the two key spaces apart.
        */
        const key = JSON.stringify(['threshold', requirement.ref.id, step.id, objective.name]);
        if (seen.has(key)) continue;
        seen.add(key);
        blockers.push({
          kind: 'threshold-without-scoring',
          criterionId: requirement.ref.id,
          criterionName: state.name,
          pin: requirement.ref.pin,
          threshold,
          ...where,
        });
      }
    }
  });

  const tokenPins = criterionPinsOf(steps);
  for (const site of directionSitesOf(steps)) {
    const step = steps[site.stepIndex];
    if (step === undefined) continue;
    const objective =
      site.objectiveId === null ? null : step.objectives.find((o) => o.id === site.objectiveId);
    for (const variable of variablesOf(site.text)) {
      if (variable.kind !== 'criterion') continue;
      check(variable.id, tokenPins.get(variable.id) ?? DEFAULT_VERSION_PIN, {
        stepId: step.id,
        stepIndex: site.stepIndex,
        stepName: step.name,
        objectiveName: objective?.name ?? null,
      });
    }
  }

  return blockers;
}

/**
 * Every criterion a protocol cites, with the pin of each citation (§4): the
 * objectives' requirements with their own pin, the direction variables at the
 * pin of the same criterion's requirement, `latest` when none (H14). What the
 * reference extractor of the store hands the usage walk, what the freeze
 * resolves, and what the editor uses to know which criteria to read.
 */
export function citedCriteria(
  steps: ProtocolSteps,
): { readonly id: string; readonly pin: VersionPin }[] {
  const cited: { id: string; pin: VersionPin }[] = [];
  const seen = new Set<string>();
  const add = (id: string, pin: VersionPin): void => {
    const key = `${id}\u0000${pinKey(pin)}`;
    if (seen.has(key)) return;
    seen.add(key);
    cited.push({ id, pin });
  };
  for (const step of steps) {
    for (const objective of step.objectives) {
      for (const requirement of objective.criteria) add(requirement.ref.id, requirement.ref.pin);
    }
  }
  const tokenPins = criterionPinsOf(steps);
  for (const site of directionSitesOf(steps)) {
    for (const variable of variablesOf(site.text)) {
      if (variable.kind === 'criterion') {
        add(variable.id, tokenPins.get(variable.id) ?? DEFAULT_VERSION_PIN);
      }
    }
  }
  return cited;
}
